import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import Avatar from '@/components/Avatar';
import StarRating from '@/components/StarRating';
import { MapPin, Eye, ShoppingBag, Sprout, ArrowUpRight, Clock } from 'lucide-react';

/**
 * Rich preview sheet triggered by long-pressing any feed card.
 * Shows expanded content without leaving the list, plus a one-tap "Open" CTA.
 * Works for both produce and requirement posts (discriminated by `kind`).
 */
export default function CardPeekSheet({ open, onOpenChange, kind, post }) {
  const navigate = useNavigate();
  if (!post) return null;

  const isProduce = kind === 'produce';
  const goDetail = () => {
    onOpenChange(false);
    navigate(isProduce ? `/produce/${post.id}` : `/requirement/${post.id}`);
  };

  const authorName = isProduce ? post.farmer_name : post.user_name;
  const authorAvatar = isProduce ? post.farmer_avatar : post.user_avatar;
  const heroImage = isProduce ? post.images?.[0] : post.image;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        data-testid="card-peek-sheet"
        className="p-0 rounded-t-3xl overflow-hidden flex flex-col max-h-[88dvh] bg-[#F6F8FB]"
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1" aria-hidden>
          <div className="w-10 h-1 rounded-full bg-[#E4E8EF]" />
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Kind pill */}
          <div className="px-6 pt-2">
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                isProduce
                  ? 'bg-[#0B4F3F] text-[#F6F8FB]'
                  : 'bg-[#0B4F3F]/10 text-[#0B4F3F]'
              }`}
            >
              {isProduce ? <Sprout className="w-3 h-3" /> : <ShoppingBag className="w-3 h-3" />}
              {isProduce ? 'Produce' : 'Requirement'}
            </span>
          </div>

          {/* Hero */}
          {heroImage ? (
            <div className="mt-4 mx-6 h-56 rounded-3xl overflow-hidden bg-[#EFF2F7] border border-[#E4E8EF]">
              <img
                src={heroImage}
                alt={post.title}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          ) : (
            <div className="mt-4 mx-6 h-40 rounded-3xl bg-[#EFF2F7] border border-[#E4E8EF] flex items-center justify-center">
              <span className="text-4xl opacity-60">{isProduce ? '🌾' : '🛒'}</span>
            </div>
          )}

          {/* Title & price */}
          <div className="px-6 pt-5">
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-xl font-bold text-[#0B1620] font-['Bricolage_Grotesque'] tracking-tight leading-snug">
                {post.title}
              </h2>
              <span
                className={`whitespace-nowrap px-3 py-1.5 rounded-full text-sm font-bold tabular-nums shadow-[0_6px_20px_-6px_rgba(11,79,63,0.35)] ${
                  isProduce
                    ? 'bg-[#0B4F3F] text-[#F6F8FB]'
                    : 'border border-[#0B4F3F]/30 bg-[#F6F8FB] text-[#0B4F3F]'
                }`}
              >
                {!isProduce && <span className="text-[10px] font-semibold uppercase mr-1 opacity-70">Offer</span>}
                ₹{post.price_per_unit}
                <span className="text-[10px] font-semibold opacity-80">/{post.unit}</span>
              </span>
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[#5B6675]">
              <span className="tabular-nums font-semibold text-[#0B1620]">{post.quantity} {post.unit}</span>
              <span className="uppercase tracking-wider">{post.category}</span>
              {post.location && (
                <span className="inline-flex items-center gap-0.5">
                  <MapPin className="w-3 h-3" /> {post.location}
                </span>
              )}
              <span className="inline-flex items-center gap-1">
                <Eye className="w-3 h-3" /> {post.views || 0}
              </span>
              {post.created_at && (
                <span className="inline-flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {new Date(post.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                </span>
              )}
            </div>
          </div>

          {/* Description */}
          {post.description && (
            <div className="px-6 pt-4">
              <p className="text-[14px] leading-relaxed text-[#0B1620]/90 whitespace-pre-wrap">
                {post.description}
              </p>
            </div>
          )}

          {/* Author */}
          <div className="px-6 pt-5 pb-4">
            <div className="flex items-center gap-3 p-3 rounded-2xl bg-[#EFF2F7] border border-[#E4E8EF]">
              <Avatar name={authorName} src={authorAvatar} size={40} className="ring-1 ring-[#E4E8EF]" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#0B1620] truncate">{authorName || (isProduce ? 'Farmer' : 'Buyer')}</p>
                {isProduce ? (
                  <div className="mt-0.5 flex items-center gap-1">
                    <StarRating value={post.farmer_avg_rating || 0} size={11} />
                    {post.farmer_review_count > 0 ? (
                      <span className="text-[10px] text-[#5B6675]">
                        <span className="font-semibold text-[#0B1620]">{post.farmer_avg_rating?.toFixed(1)}</span>
                        <span className="ml-0.5">({post.farmer_review_count})</span>
                      </span>
                    ) : (
                      <span className="text-[10px] text-[#5B6675]/70">New</span>
                    )}
                  </div>
                ) : (
                  <span className="text-[11px] text-[#5B6675] capitalize">
                    {post.user_role === 'farmer' ? 'Farmer buyer' : 'Buyer'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer CTA */}
        <div className="px-6 pb-8 pt-3 border-t border-[#E4E8EF] bg-[#F6F8FB]">
          <button
            data-testid="peek-open-btn"
            onClick={goDetail}
            className="w-full h-12 rounded-full text-sm font-semibold bg-[#0B4F3F] hover:bg-[#083D31] text-white transition-all active:scale-[0.98] shadow-[0_8px_20px_-8px_rgba(11,79,63,0.4)] flex items-center justify-center gap-1.5"
          >
            Open full post <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
